import React, { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react"
import Tour, { TourStep } from "./Tour"

export interface TourRegistration {
    plugin: string
    tabId?: string
    steps: TourStep[]
}

interface TourEducationContextValue {
    registerTour: (registration: TourRegistration) => () => void
    startTour: (plugin?: string, tabId?: string) => void
    isTourAvailable: (plugin?: string, tabId?: string) => boolean
}

const TourEducationContext = createContext<TourEducationContextValue | undefined>(undefined)

const getTourKey = (plugin?: string, tabId?: string) => `${tabId ?? ""}:${plugin ?? ""}`

const findTourKey = (registrations: Record<string, TourRegistration>, plugin?: string, tabId?: string) => {
    const exactKey = getTourKey(plugin, tabId)

    if (registrations[exactKey]) return exactKey

    return Object.keys(registrations).find((key) => registrations[key].plugin === plugin)
}

export const TourProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
    const [registrations, setRegistrations] = useState<Record<string, TourRegistration>>({})
    const [activeTourKey, setActiveTourKey] = useState<string>()
    const [isOpen, setIsOpen] = useState(false)

    const registerTour = useCallback((registration: TourRegistration) => {
        const key = getTourKey(registration.plugin, registration.tabId)

        setRegistrations((prev) => ({
            ...prev,
            [key]: registration,
        }))

        return () => {
            setRegistrations((prev) => {
                const next = { ...prev }
                delete next[key]
                return next
            })
        }
    }, [])

    const startTour = useCallback((plugin?: string, tabId?: string) => {
        const key = findTourKey(registrations, plugin, tabId)

        if (!key) return

        setActiveTourKey(key)
        setIsOpen(true)
    }, [registrations])

    const isTourAvailable = useCallback((plugin?: string, tabId?: string) => {
        return Boolean(findTourKey(registrations, plugin, tabId))
    }, [registrations])

    const activeSteps = activeTourKey ? registrations[activeTourKey]?.steps ?? [] : []

    const value = useMemo<TourEducationContextValue>(() => ({
        registerTour,
        startTour,
        isTourAvailable,
    }), [registerTour, startTour, isTourAvailable])

    return (
        <TourEducationContext.Provider value={value}>
            {children}
            <Tour
                open={isOpen}
                onClose={() => setIsOpen(false)}
                steps={activeSteps}
            />
        </TourEducationContext.Provider>
    )
}

export const useTourEducation = () => {
    const context = useContext(TourEducationContext)

    if (!context) {
        throw new Error("useTourEducation must be used inside TourProvider")
    }

    return context
}
